// Mirrored from supabase/functions/_shared/channel-health.ts (DE-567).
// FUENTE DE VERDAD: el módulo de _shared. Mantener ambos en sync.

export type ChannelHealthStatus =
  | "connected"
  | "session_expired"
  | "disconnected_from_meta"
  | "webhook_unsubscribed"
  | "missing_credentials"
  | "cannot_send";

export interface ChannelHealth {
  status: ChannelHealthStatus;
  since: string | null;
  error: string | null;
}

const CREDENTIAL_KEYS: Record<string, string[]> = {
  whatsapp: ["access_token", "phone_number_id"],
  instagram: ["instagram_access_token"],
  facebook: ["page_access_token", "page_id"],
};

export function readChannelHealth(
  channelType: string | null | undefined,
  config: Record<string, unknown> | null | undefined,
): ChannelHealth {
  const cfg = config || {};

  if (cfg.token_expired === true || cfg.token_expired_at) {
    return {
      status: "session_expired",
      since: typeof cfg.token_expired_at === "string" ? cfg.token_expired_at : null,
      error: typeof cfg.token_expired_error === "string" ? cfg.token_expired_error : null,
    };
  }

  if (cfg.meta_asset_unavailable === true) {
    return {
      status: "disconnected_from_meta",
      since: typeof cfg.meta_asset_unavailable_at === "string" ? cfg.meta_asset_unavailable_at : null,
      error: typeof cfg.meta_asset_unavailable_error === "string" ? cfg.meta_asset_unavailable_error : null,
    };
  }

  const required = CREDENTIAL_KEYS[String(channelType || "")];
  if (required?.some((key) => !cfg[key])) {
    return { status: "missing_credentials", since: null, error: null };
  }

  if (cfg.webhook_subscription_ok === false) {
    return {
      status: "webhook_unsubscribed",
      since: typeof cfg.webhook_checked_at === "string" ? cfg.webhook_checked_at : null,
      error: typeof cfg.webhook_subscription_error === "string" ? cfg.webhook_subscription_error : null,
    };
  }

  if (cfg.ig_send_surface_unsupported === true || cfg.validation_status === "needs_facebook_page") {
    return {
      status: "cannot_send",
      since: typeof cfg.ig_send_surface_flagged_at === "string" ? cfg.ig_send_surface_flagged_at : null,
      error: typeof cfg.ig_send_surface_error === "string" ? cfg.ig_send_surface_error : null,
    };
  }

  return { status: "connected", since: null, error: null };
}

export const CHANNEL_HEALTH_LABELS: Record<ChannelHealthStatus, string> = {
  connected: "Conectado",
  session_expired: "Sesión expirada",
  disconnected_from_meta: "Desconectado de Meta",
  webhook_unsubscribed: "No recibe mensajes",
  missing_credentials: "Sin credenciales",
  cannot_send: "No puede responder",
};

export const CHANNEL_HEALTH_HINTS: Record<ChannelHealthStatus, string> = {
  connected: "El canal puede enviar y recibir mensajes.",
  session_expired: "Meta cerró la sesión. El asistente no responde por este canal hasta que lo reconectes.",
  disconnected_from_meta: "Meta dejó de reconocer este número o cuenta como parte de la aplicación: no entran ni salen mensajes. Revisa en el Business Manager que siga asignado a Flow.",
  webhook_unsubscribed: "Las credenciales sirven y se pueden enviar mensajes, pero la cuenta no tiene nuestros webhooks suscritos: lo que escriban los clientes no llega. Usa \"Verificar conexión\".",
  missing_credentials: "Faltan credenciales para enviar. Completa la configuración del canal.",
  cannot_send: "No puede responder las conversaciones que llegan por su Página de Facebook. Conecta esa Página como canal de Facebook.",
};

/** Etiqueta relativa para `last_webhook_at`: la última vez que Meta nos habló. */
export function describeLastWebhook(lastWebhookAt: unknown, now = Date.now()): string | null {
  if (typeof lastWebhookAt !== "string") return null;
  const seen = Date.parse(lastWebhookAt);
  if (!Number.isFinite(seen)) return null;

  const minutes = Math.max(0, Math.round((now - seen) / 60_000));
  if (minutes < 2) return "hace un momento";
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.round(hours / 24);
  return `hace ${days} ${days === 1 ? "día" : "días"}`;
}

/**
 * Un canal que recibía a diario y lleva horas mudo está roto aunque Meta siga
 * contestando bien a las sondas — es exactamente la forma en que este fallo se
 * ve desde afuera antes de que nadie intente enviar nada.
 */
export const WEBHOOK_SILENCE_WARNING_HOURS = 12;

export function isWebhookSuspiciouslyQuiet(lastWebhookAt: unknown, now = Date.now()): boolean {
  if (typeof lastWebhookAt !== "string") return false;
  const seen = Date.parse(lastWebhookAt);
  if (!Number.isFinite(seen)) return false;
  return now - seen > WEBHOOK_SILENCE_WARNING_HOURS * 3_600_000;
}
